import OpenReplayTracker from '@openreplay/tracker'
import { InputMode } from '@openreplay/tracker/lib/modules/input'
import { createLogger } from '../utils/otel/logger'
import { HttpStatusCode } from 'axios'
import { OPENREPLAY_REDACTED_NETWORK_OPTIONS } from './openReplayNetworkCaptureRedaction'
import { OpenReplayLiveViewAssist, openReplayLiveViewAssistPlugin } from './openReplayLiveViewAssist/OpenReplayLiveViewAssist'
import { resolveOpenReplaySanitizeLevelForElement } from './openReplaySensitiveElementSanitizer'
import { OpenReplayAssistStartOutcome } from './openReplayAssistStartOutcome'

// the tracker's worker drops its sender 20ms after a stop, so a restart sooner can lose the new session's sender
const LIVE_VIEW_SESSION_RESTART_DELAY_MS = 500

// Create a logger instance for this module
const logger = createLogger({
    avenProject: 'openreplay',
    logProcessorBaseUrl: process.env.VUE_APP_LOGGER_BASE_URL ? process.env.VUE_APP_LOGGER_BASE_URL.replace(/\/logs\/?$/, '/client-logs') : undefined,
    clientSideLogsEnabled: process.env.VUE_APP_CLIENT_SIDE_LOGS_ENABLED === 'yes',
    getContext: () => ({
        service: 'openReplay',
    }),
})

// For compatibility with all Aven applications that use different implementations of httpClient
interface HttpClient {
    post(url: string, data?: any, config?: any): Promise<any>
    get(url: string, config?: any): Promise<any>
}

export interface OpenReplayInitOptions {
    /** Keep the tab's stored tracker session instead of starting from the session hash, so a Live View share's Assist room survives a reload. */
    shouldContinueStoredTrackerSession: boolean
}

export interface OpenReplayLiveViewSession {
    sessionId: string
    sessionRecordingUrl: string
}

/**
 * Unified OpenReplay class that combines functionality from all Aven applications that supports:
 * - HTTP request tracking
 * - Session recording
 * - Metadata tracking
 */
export class OpenReplay {
    public openReplayTracker: OpenReplayTracker | undefined
    private assistPlugin: OpenReplayLiveViewAssist | undefined
    private sessionId: string | undefined
    private urlSubmitHttpClient: HttpClient | undefined
    private userTraits: Record<string, any> | undefined
    private trackedHttpClients: Array<{ initializeOpenReplayRequestTracker: (tracker: OpenReplayTracker) => void }> = []
    private isRestartingForLiveViewSession = false
    private MAX_POLL_ITERATIONS = 1440 // 1440 * 5000ms = 7200 seconds = 2 hours

    constructor(private readonly projectKey: string | undefined) {
        try {
            if (this.projectKey) {
                this.openReplayTracker = new OpenReplayTracker({
                    projectKey: this.projectKey,
                    ingestPoint: 'https://openreplay.aven.com/ingest',
                    captureIFrames: false, // don't capture very sensitive info
                    obscureInputEmails: false,
                    obscureInputNumbers: true,
                    obscureInputDates: false,
                    obscureTextEmails: false,
                    obscureTextNumbers: false,
                    defaultInputMode: InputMode.Plain,
                    domSanitizer: resolveOpenReplaySanitizeLevelForElement,
                    network: OPENREPLAY_REDACTED_NETWORK_OPTIONS,
                    callOnFailureFunc: this.initWhenNetworkRestored.bind(this),
                })
                this.enableOpenReplayAssist()
            } else {
                logger.info('Skipping OpenReplayTracker instantiation on %{nodeEnv} env', { nodeEnv: process.env.VUE_APP_NODE_ENV })
            }
        } catch (error) {
            logger.error(`OpenReplayTracker failed to instantiate`, { error })
        }
    }

    /**
     * Initialize OpenReplay for a session
     * @param sessionId - The unique session identifier
     * @param userTraits - User metadata to associate with the session
     * @param trackedHttpClients - Array of HTTP clients to track (must have initializeOpenReplayRequestTracker method)
     * @param urlSubmitHttpClient - HTTP client to submit the session recording URL
     * @returns The session recording URL if available, undefined otherwise
     */
    public init = async (
        sessionId: string,
        userTraits: Record<string, any>,
        trackedHttpClients: Array<{ initializeOpenReplayRequestTracker: (tracker: OpenReplayTracker) => void }> = [],
        urlSubmitHttpClient: HttpClient,
        { shouldContinueStoredTrackerSession }: Partial<OpenReplayInitOptions> = {},
    ): Promise<string | undefined> => {
        // Check if already initialized with this session ID
        if (this.openReplayTracker && this.openReplayTracker.isActive() && this.sessionId === sessionId) {
            logger.info(`Tried to initialize openReplay twice in the same session, exiting instead`)
            return undefined
        }

        this.sessionId = sessionId
        this.userTraits = userTraits
        this.trackedHttpClients = trackedHttpClients
        this.urlSubmitHttpClient = urlSubmitHttpClient
        logger.info('OpenReplay with sessionId: %{sessionId}', { sessionId })

        try {
            let sessionRecordingUrl: string | undefined

            if (this.projectKey && this.openReplayTracker) {
                // Set up HTTP tracking for all provided clients
                if (trackedHttpClients.length > 0) {
                    logger.info(`Adding request watchers for openreplay`)
                    trackedHttpClients.forEach((client) => {
                        client.initializeOpenReplayRequestTracker(this.openReplayTracker!)
                    })
                } else {
                    logger.warn('No HTTP clients provided for openreplay, skipping request tracking')
                }

                // Start the tracker with user information
                logger.info('Starting OpenReplay, continuing the stored tracker session: %{shouldContinueStoredTrackerSession}', {
                    shouldContinueStoredTrackerSession: !!shouldContinueStoredTrackerSession,
                })
                await this.openReplayTracker.start({
                    userID: sessionId,
                    sessionHash: shouldContinueStoredTrackerSession ? undefined : sessionId, // enables cross domain linking of sessions
                    metadata: userTraits,
                })

                logger.info(`OpenReplay started`)
                logger.info('Attempting to get openreplay sessionRecordingUrl for sessionID %{sessionId}', { sessionId })
                sessionRecordingUrl = this.openReplayTracker.getSessionURL()

                // Submit the session recording URL to the backend
                await urlSubmitHttpClient.post('/ana/sessionRecordingUrl', { sessionId, sessionRecordingUrl })
            } else {
                logger.info('Skipping OpenReplayTracker initialization on %{nodeEnv} env', { nodeEnv: process.env.VUE_APP_NODE_ENV })
                sessionRecordingUrl = 'local dev test openreplay session url'
            }

            // Each application will handle storage of the session recording URL in their own way
            return sessionRecordingUrl
        } catch (error) {
            logger.error('OpenReplayTracker failed to initialize for sessionId: %{sessionId}.', { sessionId, error })
            return undefined
        }
    }

    public enableOpenReplayAssist = () => {
        if (!this.openReplayTracker) {
            logger.error(`Tried to enable OpenReplay Assist with uninstantiated OpenReplay.`)
            return
        }

        // If assist plugin is already created, don't enable it again
        if (this.assistPlugin) {
            logger.info(`OpenReplay Assist already created, skipping`)
            return
        }

        this.assistPlugin = this.openReplayTracker.use(openReplayLiveViewAssistPlugin())
        logger.info(`OpenReplay Assist enabled`)
    }

    public startAssist = async (): Promise<OpenReplayAssistStartOutcome> => {
        if (!this.assistPlugin) {
            logger.warn('Tried to start OpenReplay Assist without an assist plugin, tracker instantiated: %{isTrackerInstantiated}', { isTrackerInstantiated: !!this.openReplayTracker })
            return OpenReplayAssistStartOutcome.UNAVAILABLE
        }
        const outcome = await this.assistPlugin.start()
        logger.info('Started OpenReplay Assist: %{outcome}', { outcome })
        return outcome
    }

    /** Disconnects Assist and keeps it off, even across tracker restarts, until startAssist() or a Live View session restart turns it back on. */
    public stopAssist = async (): Promise<void> => {
        if (!this.assistPlugin) {
            logger.info('No OpenReplay Assist plugin to stop')
            return
        }
        await this.assistPlugin.stop()
        logger.info('Stopped OpenReplay Assist')
    }

    /**
     * Restarts recording in a new OpenReplay session and reports its url. Live View does this when a share starts and when it
     * ends: the share gets an empty Assist room, and anyone who watched it is left in a room that never gets frames again.
     * Assist stays on and reconnects in the new session.
     */
    public startNewSessionForLiveView = async (): Promise<OpenReplayLiveViewSession | null> => {
        const tracker = this.openReplayTracker
        const sessionId = this.sessionId
        const urlSubmitHttpClient = this.urlSubmitHttpClient
        if (!tracker || !sessionId || !urlSubmitHttpClient) {
            logger.warn('Cannot start a new OpenReplay session for Live View: tracker %{hasTracker}, session %{hasSession}', { hasTracker: !!tracker, hasSession: !!sessionId })
            return null
        }
        this.isRestartingForLiveViewSession = true
        try {
            // Assist must not keep a socket in the old session's room, even one held through a restart for a newly joined agent
            await this.assistPlugin?.stop()
            tracker.stop()
            await new Promise((resolve) => setTimeout(resolve, LIVE_VIEW_SESSION_RESTART_DELAY_MS))
            const startResult = await tracker.start({ userID: sessionId, metadata: this.userTraits, forceNew: true })
            if (!startResult.success) {
                logger.warn('OpenReplay did not start a new session for Live View: %{startResult}', { startResult })
                return null
            }
            const sessionRecordingUrl = tracker.getSessionURL()
            if (!sessionRecordingUrl) {
                logger.warn('OpenReplay started session %{openReplaySessionId} for Live View without a session url', { openReplaySessionId: startResult.sessionID })
                return null
            }
            await urlSubmitHttpClient.post('/ana/sessionRecordingUrl', { sessionId, sessionRecordingUrl })
            logger.info('Started OpenReplay session %{openReplaySessionId} for Live View', { openReplaySessionId: startResult.sessionID })
            return { sessionId: startResult.sessionID, sessionRecordingUrl }
        } catch (error) {
            logger.error('Failed to start a new OpenReplay session for Live View', { error })
            return null
        } finally {
            this.isRestartingForLiveViewSession = false
            void this.assistPlugin?.start()
            if (!tracker.isActive()) {
                // the guard above made the tracker skip its own recovery, so hand a failed rotation back to it; it starts a fresh session
                void this.initWhenNetworkRestored()
            }
        }
    }

    public getTrackerSessionId = (): string | null => {
        return this.openReplayTracker?.getSessionID() ?? null
    }

    public getIsTrackerActive = (): boolean => {
        return this.openReplayTracker?.isActive() ?? false
    }

    public getTabId = (): string | null => {
        return this.openReplayTracker?.getTabId() ?? null
    }

    /**
     * Set metadata on the OpenReplay tracker
     * @param metadata - Key-value pairs to set as metadata
     * @param sessionIdParam - Optional session ID (uses the current session ID if not provided)
     * @returns boolean indicating success
     */
    public trySetMetadata = (metadata: Record<string, any>, sessionIdParam?: string): boolean => {
        // If a specific session ID is provided, check if it matches the current session
        if (sessionIdParam && this.sessionId !== sessionIdParam) {
            logger.error(`Tried to set metadata for a different session, exiting instead`)
            return false
        }

        if (!this.openReplayTracker) {
            logger.error(`Tried to set metadata with uninitialized openreplay exiting instead`)
            return false
        }

        try {
            logger.info('Trying to set metadata on openreplay: %{metadata}', { metadata })
            for (const [trait, value] of Object.entries(metadata)) {
                this.openReplayTracker.setMetadata(trait, value)
            }
            // kept so a session started later (a Live View share or a re-init) records the same metadata
            this.userTraits = { ...this.userTraits, ...metadata }
            return true
        } catch (error) {
            logger.error('failed to setMetadata on openreplay: %{metadata}.', { metadata, error })
            return false
        }
    }

    /**
     * Force flush the batch on openreplay - this helps prevent data loss in the event of tab or iframe close
     * @param timeoutMs - Optional timeout in milliseconds. If not provided, will wait indefinitely for the flush to complete
     * @returns Promise that resolves to true if successful, false otherwise
     */
    public forceFlushBatch = (timeoutMs?: number): Promise<boolean> => {
        return new Promise((resolve) => {
            if (!this.openReplayTracker) {
                logger.error(`Tried to flush batch with uninitialized openreplay exiting instead`)
                resolve(false)
                return
            }
            try {
                logger.info(`Trying to flush batch on openreplay`)
                let timeout: ReturnType<typeof setTimeout> | undefined

                if (timeoutMs !== undefined) {
                    timeout = setTimeout(() => {
                        logger.info('forceFlushBatch timed out after %{timeoutMs}ms, resolving as false', { timeoutMs })
                        resolve(false)
                    }, timeoutMs)
                }

                this.openReplayTracker.forceFlushBatch((success) => {
                    if (timeout) {
                        clearTimeout(timeout)
                    }
                    if (success) {
                        logger.info(`forceFlushBatch completed on openreplay`)
                        resolve(true)
                    } else {
                        logger.error(`forceFlushBatch failed on openreplay`)
                        resolve(false)
                    }
                })
            } catch (error) {
                logger.error(`failed to forceFlushBatch on openreplay.`, { error })
                resolve(false)
            }
        })
    }

    public initWhenNetworkRestored = async (): Promise<void> => {
        for (let i = 0; i < this.MAX_POLL_ITERATIONS; i++) {
            if (this.assistPlugin && this.assistPlugin.forcingTrackerRestart) {
                logger.info(`OpenReplay restart was assist-driven, not reinitializing`)
                return
            }

            if (this.isRestartingForLiveViewSession) {
                logger.info(`OpenReplay restart is starting a Live View session, not reinitializing`)
                return
            }

            if (this.openReplayTracker && this.openReplayTracker.isActive() && this.sessionId) {
                logger.info(`Openreplay already active, no need to re-init`)
                return
            }

            if (!this.urlSubmitHttpClient) {
                logger.error('Tried to reinit openreplay with uninitialized urlSubmitHttpClient exiting instead')
                return
            }

            logger.info(`OpenReplay: Checking if endpoint is reachable`)
            try {
                const response = await this.urlSubmitHttpClient.get('/')
                if (response.status === HttpStatusCode.Ok) {
                    logger.info('Internet is truly reachable!')
                    if (this.sessionId && this.userTraits && this.trackedHttpClients && this.urlSubmitHttpClient) {
                        await this.init(this.sessionId, this.userTraits, this.trackedHttpClients, this.urlSubmitHttpClient)
                    } else {
                        logger.error('Tried to reinit openreplay with uninitialized sessionId, userTraits, trackedHttpClients, or urlSubmitHttpClient.')
                    }
                    return
                } else {
                    logger.info('Connected, but no internet access.')
                }
            } catch (e) {
                logger.error('Fetch failed — still no internet.', { error: e })
            }

            await new Promise((r) => {
                setTimeout(r, 5000)
            })
        }
    }
}
